import React, { useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  List, 
  ListOrdered, 
  Quote, 
  Link as LinkIcon,
  Code,
  Type
} from 'lucide-react';
import { Label } from '@/components/ui/label';

interface NotificationTemplateEditorProps {
  value: string;
  onChange: (html: string) => void;
  variables?: string[];
  placeholder?: string;
}

export const NotificationTemplateEditor: React.FC<NotificationTemplateEditorProps> = ({
  value,
  onChange,
  variables = [],
  placeholder = "Enter your template content..."
}) => {
  const [activeTab, setActiveTab] = useState<'editor' | 'html'>('editor');
  const [htmlValue, setHtmlValue] = useState(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Underline,
      Link.configure({ openOnClick: false }),
    ],
    content: value,
    onUpdate({ editor }) {
      const html = editor.getHTML();
      onChange(html);
      setHtmlValue(html);
    },
    editorProps: {
      attributes: {
        class: 'prose max-w-none min-h-[200px] p-4 border rounded bg-background focus:outline-none text-foreground prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-em:text-foreground prose-code:text-foreground prose-pre:bg-muted prose-pre:text-foreground prose-blockquote:text-muted-foreground prose-li:text-foreground [&_*]:text-foreground',
        placeholder,
      },
    },
  });

  const insertVariable = (variable: string) => {
    if (editor) {
      editor.chain().focus().insertContent(`{${variable}}`).run();
    }
  };

  const handleHtmlChange = (newHtml: string) => {
    setHtmlValue(newHtml);
    onChange(newHtml);
    if (editor) {
      editor.commands.setContent(newHtml);
    }
  };

  const syncHtmlToEditor = () => {
    if (editor) {
      editor.commands.setContent(htmlValue);
    }
  };

  if (!editor) return <div>Loading editor...</div>;

  return (
    <div className="space-y-4">
      {/* Variable Insertion */}
      {variables.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Available Variables:</Label>
          <div className="flex flex-wrap gap-2">
            {variables.map((variable) => (
              <Badge
                key={variable}
                variant="outline"
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                onClick={() => insertVariable(variable)}
              >
                {`{${variable}}`}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Editor Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'editor' | 'html')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="editor" className="flex items-center gap-2">
            <Type className="h-4 w-4" />
            Rich Editor
          </TabsTrigger>
          <TabsTrigger value="html" className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            HTML Code
          </TabsTrigger>
        </TabsList>

        <TabsContent value="editor" className="space-y-2">
          {/* Toolbar */}
          <div className="flex flex-wrap gap-1 border rounded p-2 bg-muted/50">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={editor.isActive('bold') ? 'bg-primary text-primary-foreground' : ''}
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={editor.isActive('italic') ? 'bg-primary text-primary-foreground' : ''}
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={editor.isActive('underline') ? 'bg-primary text-primary-foreground' : ''}
            >
              <UnderlineIcon className="h-4 w-4" />
            </Button>
            <div className="w-px h-6 bg-border mx-1" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={editor.isActive('bulletList') ? 'bg-primary text-primary-foreground' : ''}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={editor.isActive('orderedList') ? 'bg-primary text-primary-foreground' : ''}
            >
              <ListOrdered className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={editor.isActive('blockquote') ? 'bg-primary text-primary-foreground' : ''}
            >
              <Quote className="h-4 w-4" />
            </Button>
            <div className="w-px h-6 bg-border mx-1" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const url = prompt('Enter URL');
                if (url) {
                  editor.chain().focus().setLink({ href: url }).run();
                }
              }}
              className={editor.isActive('link') ? 'bg-primary text-primary-foreground' : ''}
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Editor Content */}
          <EditorContent editor={editor} />
        </TabsContent>

        <TabsContent value="html" className="space-y-2">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium">HTML Code:</Label>
              <Button size="sm" variant="outline" onClick={syncHtmlToEditor}>
                Sync to Editor
              </Button>
            </div>
            <Textarea
              value={htmlValue}
              onChange={(e) => handleHtmlChange(e.target.value)}
              placeholder="Enter HTML code..."
              className="min-h-[200px] font-mono text-sm"
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}; 